import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { EASE, INTRO } from "./Reveal";
import { HeroParticles } from "./HeroParticles";
import { Magnetic } from "./Magnetic";
import { IMG, scrollToId } from "./data";
import { Seal } from "./Seal";

const LINES = [
  <>Metal, moved</>,
  <>with <em className="gold-shimmer font-serif font-normal italic">trust</em></>,
  <>across oceans.</>,
];

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: INTRO + 0.15 + i * 0.12, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const TiltFrame = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 800], [0, 120]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="relative [perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset} ref={ref} data-testid="hero-tilt-frame">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 24px 24px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 24px 24px)" }}
          transition={{ duration: 1.4, delay: INTRO + 0.3, ease: EASE }}
          className="relative aspect-[4/5] w-full overflow-hidden bg-navy"
        >
          <motion.img style={{ y: imgY }} src={IMG.hero} alt="Container vessel leaving port" className="absolute inset-0 h-[115%] w-full scale-110 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-white">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em]">Sourced globally<br />Delivered to India</p>
            <p className="font-serif text-3xl italic">No. 01</p>
          </div>
        </motion.div>
        <motion.div style={{ transform: "translateZ(80px)" }} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: INTRO + 1.1, duration: 0.9, ease: EASE }}
          className="absolute -left-6 top-[22%] rounded-2xl border border-white/60 bg-white/85 px-4 py-3 shadow-xl backdrop-blur-md sm:-left-12" data-testid="hero-chip-copper">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Core focus</p>
          <p className="font-display text-lg font-bold text-navy"><span className="text-copper">Cu</span> Copper</p>
        </motion.div>
        <motion.div style={{ transform: "translateZ(120px)" }} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: INTRO + 1.25, duration: 0.9, ease: EASE }}
          className="absolute -right-4 top-[55%] rounded-2xl border border-white/60 bg-white/85 px-4 py-3 shadow-xl backdrop-blur-md sm:-right-10" data-testid="hero-chip-aluminium">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Core focus</p>
          <p className="font-display text-lg font-bold text-navy"><span className="text-slate-400">Al</span> Aluminium</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

const onGlow = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--gx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--gy", `${e.clientY - r.top}px`);
};

export const Hero = () => (
  <section id="top" onMouseMove={onGlow} className="hero-glow relative overflow-hidden bg-white pt-[140px] lg:pt-[150px]" data-testid="hero-section">
    <div className="grid-lines pointer-events-none absolute inset-0" />
    <HeroParticles />
    <div className="relative mx-auto grid max-w-[1400px] gap-14 px-5 pb-20 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-28">
      <div className="flex flex-col justify-center lg:col-span-7">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: INTRO, duration: 0.8 }} className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-slate-600">
          <span className="mr-3 inline-block h-2 w-2 rounded-full bg-copper align-middle" />International metal trading · Indenting
        </motion.p>
        <h1 className="font-display text-[3.1rem] font-extrabold leading-[0.95] tracking-[-0.035em] text-navy sm:text-7xl lg:text-[6.2rem]" data-testid="hero-heading">
          {LINES.map((l, i) => <MaskLine key={i} i={i}>{l}</MaskLine>)}
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: INTRO + 0.75, duration: 0.9, ease: EASE }} className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
          Zenura Metals connects reliable international scrap suppliers, recycling companies and yards with buyers in India and other markets — with a core focus on aluminium and copper.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: INTRO + 0.9, duration: 0.9, ease: EASE }} className="mt-10 flex flex-wrap items-center gap-4">
          <Magnetic>
            <button onClick={() => scrollToId("contact")} data-testid="hero-enquiry-button" className="group flex items-center gap-3 rounded-full bg-navy py-2 pl-6 pr-2 text-sm font-semibold text-white shadow-[0_18px_40px_-18px_rgba(10,26,51,0.7)] transition-colors hover:bg-gold">
              Start a trade enquiry
              <span className="grid h-10 w-10 place-items-center rounded-full bg-white/10 transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="h-4 w-4" /></span>
            </button>
          </Magnetic>
          <button onClick={() => scrollToId("materials")} data-testid="hero-materials-button" className="group flex items-center gap-2 px-2 py-3 text-sm font-semibold text-navy">
            <span className="link-underline">Explore materials</span>
            <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
          </button>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: INTRO + 1.2, duration: 1 }} className="mt-16 grid max-w-xl grid-cols-3 gap-6 border-t border-navy/10 pt-6">
          {[["Focus", "Non-ferrous"], ["Markets", "India & beyond"], ["Model", "Relationship-led"]].map(([k, v]) => (
            <div key={k}>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">{k}</p>
              <p className="mt-1 font-display text-sm font-bold text-navy sm:text-base">{v}</p>
            </div>
          ))}
        </motion.div>
      </div>
      <div className="relative mx-auto w-full max-w-[460px] lg:col-span-5 lg:max-w-none lg:pl-10">
        <TiltFrame />
        <div className="absolute -bottom-10 -left-2 z-10 lg:-left-6"><Seal /></div>
      </div>
    </div>
  </section>
);
